export const EFFECT_MANIFEST = Object.freeze({
  id: 'tone.vibrato',
  label: 'Vibrato',
  version: '1.0.1',
  inputParam: 'inputParam',
  dimensionId: 'EW::II',
  dimensionLabel: 'EW::II',
  defaults: Object.freeze({
    frequency: 5,
    depth: 0.2,
    wet: 0.5,
    type: 'sine',
  }),
  userParamSpec: Object.freeze({
    id: 'inputParam',
    label: 'Lunar Flutter',
    units: '%',
    range: Object.freeze({ min: -100, max: 100 }),
  }),
  modules: Object.freeze([
    Object.freeze({
      id: 'slow',
      label: 'Lunar Flutter Slow',
      description: 'Gentle lunar sway, dream detune.',
      dimension: 'EW::II',
      fixed: Object.freeze({ type: 'sine' }),
      target: 'depth',
      valueRange: Object.freeze({ min: -100, max: 100, units: '%' }),
      initialRange: Object.freeze({ min: -50, max: 60, equilibrium: 0 }),
      segments: Object.freeze({
        negative: Object.freeze({
          label: 'Dreamy',
          control: Object.freeze({
            mode: 'hybrid',
            audioParam: 'depth',
            provider: 'node',
            smoothing: Object.freeze({
              defaultRamp: 0.06,
              minRamp: 0.02,
              maxRamp: 0.6,
              curve: 'linear',
            }),
            signalRange: Object.freeze({ min: 0, max: 1.0, transform: 'easeOut' }),
          }),
          parameterMappings: Object.freeze({
            frequency: { min: 4, max: 0.2 },
            depth: { min: 1.0, max: 0.1 },
            wet: { min: 1.0, max: 0.0 },
          }),
        }),
        positive: Object.freeze({
          label: 'Subtle',
          control: Object.freeze({
            mode: 'hybrid',
            audioParam: 'depth',
            provider: 'node',
            smoothing: Object.freeze({
              defaultRamp: 0.06,
              minRamp: 0.02,
              maxRamp: 0.6,
              curve: 'linear',
            }),
            signalRange: Object.freeze({ min: 0.1, max: 1.0, transform: 'easeOut' }),
          }),
          parameterMappings: Object.freeze({
            frequency: { min: 0.2, max: 4 },
            depth: { min: 0.1, max: 1.0 },
            wet: { min: 0.0, max: 1.0 },
          }),
        }),
      }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'depth',
        provider: 'node',
        smoothing: Object.freeze({
          defaultRamp: 0.06,
          minRamp: 0.02,
          maxRamp: 0.6,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0, max: 100, transform: 'easeOut' }),
        secondaryParameters: Object.freeze(['frequency', 'wet']),
      }),
    }),
    Object.freeze({
      id: 'wide',
      label: 'Lunar Flutter Wide',
      description: 'Dramatic sci-fi wobble, psychedelic lead voice.',
      dimension: 'EW::II',
      fixed: Object.freeze({ type: 'sine' }),
      target: 'depth',
      valueRange: Object.freeze({ min: -100, max: 100, units: '%' }),
      initialRange: Object.freeze({ min: -60, max: 100, equilibrium: 0 }),
      segments: Object.freeze({
        negative: Object.freeze({
          label: 'Dramatic',
          control: Object.freeze({
            mode: 'hybrid',
            audioParam: 'depth',
            provider: 'node',
            smoothing: Object.freeze({
              defaultRamp: 0.06,
              minRamp: 0.02,
              maxRamp: 0.6,
              curve: 'linear',
            }),
            signalRange: Object.freeze({ min: 0, max: 1.0, transform: 'linear' }),
          }),
          parameterMappings: Object.freeze({
            frequency: { min: 8, max: 1 },
            depth: { min: 1.0, max: 0.2 },
            wet: { min: 1.0, max: 0.0 },
          }),
        }),
        positive: Object.freeze({
          label: 'Psychedelic',
          control: Object.freeze({
            mode: 'hybrid',
            audioParam: 'depth',
            provider: 'node',
            smoothing: Object.freeze({
              defaultRamp: 0.06,
              minRamp: 0.02,
              maxRamp: 0.6,
              curve: 'linear',
            }),
            signalRange: Object.freeze({ min: 0.2, max: 1.0, transform: 'linear' }),
          }),
          parameterMappings: Object.freeze({
            frequency: { min: 1, max: 8 },
            depth: { min: 0.2, max: 1.0 },
            wet: { min: 0.0, max: 1.0 },
          }),
        }),
      }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'depth',
        provider: 'node',
        smoothing: Object.freeze({
          defaultRamp: 0.06,
          minRamp: 0.02,
          maxRamp: 0.6,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0, max: 100, transform: 'linear' }),
        secondaryParameters: Object.freeze(['frequency', 'wet']),
      }),
    }),
    Object.freeze({
      id: 'nervous',
      label: 'Lunar Flutter Nervous',
      description: 'Fast micro pitch; anxious/robotic flutter.',
      dimension: 'EW::II',
      fixed: Object.freeze({ type: 'sine' }),
      target: 'frequency',
      valueRange: Object.freeze({ min: -100, max: 100, units: '%' }),
      initialRange: Object.freeze({ min: -40, max: 100, equilibrium: 0 }),
      segments: Object.freeze({
        negative: Object.freeze({
          label: 'Rapid',
          control: Object.freeze({
            mode: 'hybrid',
            audioParam: 'frequency',
            provider: 'node',
            smoothing: Object.freeze({
              defaultRamp: 0.05,
              minRamp: 0.01,
              maxRamp: 0.5,
              curve: 'linear',
            }),
            signalRange: Object.freeze({ min: 0, max: 16, transform: 'exp' }),
          }),
          parameterMappings: Object.freeze({
            frequency: { min: 16, max: 2 },
            depth: { min: 0.7, max: 0.2 },
            wet: { min: 1.0, max: 0.0 },
          }),
        }),
        positive: Object.freeze({
          label: 'Robotic',
          control: Object.freeze({
            mode: 'hybrid',
            audioParam: 'frequency',
            provider: 'node',
            smoothing: Object.freeze({
              defaultRamp: 0.05,
              minRamp: 0.01,
              maxRamp: 0.5,
              curve: 'linear',
            }),
            signalRange: Object.freeze({ min: 2, max: 16, transform: 'exp' }),
          }),
          parameterMappings: Object.freeze({
            frequency: { min: 2, max: 16 },
            depth: { min: 0.2, max: 0.7 },
            wet: { min: 0.0, max: 1.0 },
          }),
        }),
      }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'frequency',
        provider: 'node',
        smoothing: Object.freeze({
          defaultRamp: 0.05,
          minRamp: 0.01,
          maxRamp: 0.5,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0, max: 100, transform: 'exp' }),
        secondaryParameters: Object.freeze(['depth', 'wet']),
      }),
    }),
    Object.freeze({
      id: 'tape',
      label: 'Lunar Flutter Tape',
      description: 'Analog wow/flutter; warped cassette feel.',
      dimension: 'EW::II',
      fixed: Object.freeze({ type: 'sine' }),
      target: 'frequency',
      valueRange: Object.freeze({ min: -100, max: 100, units: '%' }),
      initialRange: Object.freeze({ min: -50, max: 50, equilibrium: 0 }),
      segments: Object.freeze({
        negative: Object.freeze({
          label: 'Warped',
          control: Object.freeze({
            mode: 'hybrid',
            audioParam: 'frequency',
            provider: 'node',
            smoothing: Object.freeze({
              defaultRamp: 0.06,
              minRamp: 0.02,
              maxRamp: 0.6,
              curve: 'linear',
            }),
            signalRange: Object.freeze({ min: 0, max: 2.5, transform: 'log' }),
          }),
          parameterMappings: Object.freeze({
            frequency: { min: 2.5, max: 0.1 },
            depth: { min: 0.6, max: 0.1 },
            wet: { min: 1.0, max: 0.0 },
          }),
        }),
        positive: Object.freeze({
          label: 'Vintage',
          control: Object.freeze({
            mode: 'hybrid',
            audioParam: 'frequency',
            provider: 'node',
            smoothing: Object.freeze({
              defaultRamp: 0.06,
              minRamp: 0.02,
              maxRamp: 0.6,
              curve: 'linear',
            }),
            signalRange: Object.freeze({ min: 0.1, max: 2.5, transform: 'log' }),
          }),
          parameterMappings: Object.freeze({
            frequency: { min: 0.1, max: 2.5 },
            depth: { min: 0.1, max: 0.6 },
            wet: { min: 0.0, max: 1.0 },
          }),
        }),
      }),
      control: Object.freeze({
        mode: 'hybrid',
        audioParam: 'frequency',
        provider: 'node',
        smoothing: Object.freeze({
          defaultRamp: 0.06,
          minRamp: 0.02,
          maxRamp: 0.6,
          curve: 'linear',
        }),
        signalRange: Object.freeze({ min: 0, max: 100, transform: 'log' }),
        secondaryParameters: Object.freeze(['depth', 'wet']),
      }),
    }),
  ]),
});

export default EFFECT_MANIFEST;
